import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Home, ChevronRight } from 'lucide-react';

const PageBreadcrumb = ({ items = [], showBackButton = true, rightContent = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isIdSegment = (segment) => {
    // MongoDB ObjectId (24 hex chars)
    return /^[a-f0-9]{24}$/i.test(segment);
  };

  // Auto-generate breadcrumb from current path if no items provided
  const generateBreadcrumbFromPath = () => {
    const pathSegments = location.pathname
      .split('/')
      .filter((segment) => segment);
    const breadcrumbItems = [];

    // Map path segments to readable labels
    const pathLabels = {
      overview: 'Overview',
      dashboard: 'Dashboard',
      assets: 'Assets',
      contracts: 'Contracts',
      finance: 'Finance',
      insights: 'Insights',
      ai: 'AI & Intelligence',
      collaboration: 'Collaboration',
      integration: 'Integration',
      projects: 'Projects',
      system: 'System',
      management: 'Management',
      lifecycle: 'Lifecycle',
      templates: 'Templates',
      'e-signature': 'E-Signature',
      renewals: 'Renewals',
      compliance: 'Compliance',
      assignment: 'Assignment & Usage',
      maintenance: 'Maintenance & Ops',
      financial: 'Financial & Valuation',
      categorization: 'Categorization',
      tasks: 'Task Management',
      team: 'Team Collaboration',
      create: 'Create',
      edit: 'Edit',
      view: 'View',
      milestones: 'Milestone Tracking',
      risks: 'Risk Management',
      'time-tracking': 'Time Tracking',
      reports: 'Reports',
      settings: 'Settings',
      users: 'User Management',
      roles: 'Role Management',
      departments: 'Department Management',
      monitoring: 'System Monitoring',
      'master-data': 'Master Data',
      'clause-library': 'Clause Library',
      erp: 'ERP Integration',
      procurement: 'Procurement Integration',
      crm: 'CRM Integration',
      sso: 'SSO & Access Control',
      api: 'API Management',
      hub: 'Collaboration Hub',
      versioning: 'Version Control',
      audit: 'Audit Trails',
      access: 'Access Controls',
      dashboards: 'Dashboards',
      analytics: 'Analytics',
      configuration: 'Application Configuration',
      support: 'Support',
      'create-ticket': 'Create Ticket',
    };

    // Always start with Dashboard (use /overview as it's the default landing page)
    breadcrumbItems.push({
      label: 'Dashboard',
      path: '/overview',
      icon: <Home size={16} />,
    });

    // Map segments to their actual route paths (some intermediate paths don't exist)
    const routeMapping = {
      tasks: '/tasks/management',
      milestones: '/milestones',
      team: '/team',
      projects: '/projects',
      assets: '/assets/management',
      contracts: '/contracts/management',
      insights: '/insights/dashboards',
      reports: '/insights/reports',
      ai: '/ai',
      finance: '/finance',
      collaboration: '/collaboration',
      integration: '/integration',
      system: '/system',
      subcontracting: '/subcontracting',
      'time-tracking': '/projects/time-tracking',
    };

    // Add other path segments with smart path building
    let currentPath = '';
    const visibleSegments = pathSegments.filter(
      (segment) => !isIdSegment(segment)
    );


    visibleSegments.forEach((segment, index) => {
      // Check if this segment has a mapped route
      const mappedRoute = routeMapping[segment];
      if (mappedRoute) {
        // Use the mapped route for this segment
        currentPath = mappedRoute;
      } else {
        // Build path incrementally
        currentPath += `/${segment}`;
      }
      if (isIdSegment(segment)) {
        return;
      }

      let label =
        pathLabels[segment] ||
        segment.charAt(0).toUpperCase() + segment.slice(1);

      // Skip if it's the same as Dashboard or Overview
      if (segment !== 'dashboard' && segment !== 'overview') {
        // For the last segment, use the actual current path
        // For intermediate segments with mappings, use the mapped route
        const breadcrumbPath = mappedRoute || currentPath;

        breadcrumbItems.push({
          label,
          path: breadcrumbPath,
          isLast: index === visibleSegments.length - 1,
        });
      }
    });

    return breadcrumbItems;
  };

  const breadcrumbItems =
    items.length > 0 ? items : generateBreadcrumbFromPath();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/overview');
    }
  };

  const handleBreadcrumbClick = (path) => {
    if (path) {
      navigate(path);
    }
  };

  // Calculate height to match sidebar menu item + 7px
  // Sidebar menu item: padding '10px 16px' + content (icon 20px) = ~40px total
  // Increased by 7px as requested
  const breadcrumbHeight = '50px';

  return (
    <div
      className="page-breadcrumb-container"
      style={{
        height: breadcrumbHeight,
        backgroundColor: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTop: '2px solid rgba(0, 0, 0, 0.08)',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        fontFamily: 'var(--font-family)',
        padding: '10px 16px', // Match sidebar menu item padding exactly
        margin: 0, // No margin to start right after header
        borderRadius: 0, // No rounded corners
        boxShadow: 'none', // No shadow
        borderLeft: 'none', // Remove left border
        borderRight: 'none', // Remove right border
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {/* Back Button */}
        {showBackButton && (
          <button
            onClick={handleBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              border: '1px solid #E5E7EB',
              borderRadius: 0, // No rounded corners
              backgroundColor: '#FFFFFF',
              color: '#6B7280',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              padding: 0,
              marginRight: '12px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#9CA3AF';
              e.currentTarget.style.backgroundColor = '#F9FAFB';
              e.currentTarget.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.backgroundColor = '#FFFFFF';
              e.currentTarget.style.color = '#6B7280';
            }}
          >
            <ArrowLeft size={16} />
          </button>
        )}

        {/* Breadcrumb Navigation */}
        <nav
          className="page-breadcrumb-nav"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontFamily: 'var(--font-family)',
            flexWrap: 'wrap',
          }}
        >
          {breadcrumbItems.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && (
                <ChevronRight
                  size={14}
                  style={{
                    color: '#6B7280',
                    flexShrink: 0,
                    margin: '0 4px',
                  }}
                />
              )}
              <div
                className={`breadcrumb-item ${item.isLast ? 'breadcrumb-current' : 'breadcrumb-link'
                  }`}
                onClick={() => !item.isLast && handleBreadcrumbClick(item.path)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 8px',
                  borderRadius: 0, // No rounded corners
                  cursor: item.isLast ? 'default' : 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: 'transparent',
                  color: item.isLast ? '#111827' : '#6B7280',
                  fontWeight: item.isLast ? '600' : '500',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  if (!item.isLast) {
                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                    e.currentTarget.style.color = '#374151';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!item.isLast) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#6B7280';
                  }
                }}
              >
                {item.icon && (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: item.isLast ? '#374151' : '#6B7280',
                    }}
                  >
                    {React.cloneElement(item.icon, { size: 14 })}
                  </span>
                )}
                <span>{item.label}</span>
              </div>
            </React.Fragment>
          ))}
        </nav>
      </div>

      {rightContent && (
        <div className="page-breadcrumb-right-content">
          {rightContent}
        </div>
      )}
    </div>
  );
};

export default PageBreadcrumb;
